import { T, useGT } from 'gt-react';
import { FunnelIcon, XIcon } from '@phosphor-icons/react';
import { REPORT_MAX_FILTERS, type ReportFilter } from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useReportDatasets,
  type ReportDatasetColumn,
} from '../ReportDataContext';
import { IconTooltip } from '@/components/common/IconTooltip';

const ADD = '__add__';

/** The filter a column gets: a date range for a Date, a number range for a Number, else a value list. */
export function newReportFilter(
  column: Pick<ReportDatasetColumn, 'key' | 'dataType'>,
  id: string,
): ReportFilter {
  if (column.dataType === 'date')
    return { id, column: column.key, kind: 'date-range' };
  if (column.dataType === 'number')
    return { id, column: column.key, kind: 'number-range' };
  return { id, column: column.key, kind: 'values', values: [] };
}

/** Every column of the surface's datasets, once per name, as filters bind by name. */
export function filterableColumns(
  datasets: readonly { columns: readonly ReportDatasetColumn[] }[],
): ReportDatasetColumn[] {
  const byKey = new Map<string, ReportDatasetColumn>();
  for (const dataset of datasets)
    for (const column of dataset.columns)
      if (!byKey.has(column.key)) byKey.set(column.key, column);
  return [...byKey.values()];
}

/**
 * The Report Builder's Filters: add one per column (bound by name, so it
 * narrows every component whose dataset has that column), rename or remove
 * it. Its value is set from the filter bar above the grid.
 */
export function ReportFilterSettings({
  filters,
  onChange,
}: {
  filters: readonly ReportFilter[];
  onChange: (filters: ReportFilter[]) => void;
}) {
  const gt = useGT();
  const { datasets } = useReportDatasets();
  const columns = filterableColumns(datasets);
  const used = new Set(filters.map((filter) => filter.column));
  const available = columns.filter((column) => !used.has(column.key));
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">
        <T context="report filters settings label">Filters</T>
      </Label>
      {filters.map((filter, index) => (
        <div key={filter.id} className="flex items-center gap-1">
          <FunnelIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <Input
            className="h-7 min-w-0 flex-1 text-xs"
            aria-label={gt('Filter label for {column}', {
              column: filter.column,
            })}
            placeholder={filter.column}
            value={filter.label ?? ''}
            onChange={(event) =>
              onChange(
                filters.map((f, i) =>
                  i === index ? { ...f, label: event.target.value } : f,
                ),
              )
            }
          />
          <IconTooltip>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              aria-label={gt('Remove the {column} filter', {
                column: filter.column,
              })}
              onClick={() => onChange(filters.filter((_, i) => i !== index))}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </IconTooltip>
        </div>
      ))}
      {filters.length < REPORT_MAX_FILTERS && available.length > 0 && (
        <Select
          value={ADD}
          onValueChange={(key) => {
            const column = available.find((c) => c.key === key);
            if (column)
              onChange([
                ...filters,
                newReportFilter(column, crypto.randomUUID()),
              ]);
          }}
        >
          <SelectTrigger
            className="h-8 text-xs"
            aria-label={gt('Add a filter', { context: 'report filters' })}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ADD} disabled>
              <T context="report filters add placeholder">Add a filter on…</T>
            </SelectItem>
            {available.map((column) => (
              <SelectItem key={column.key} value={column.key}>
                {column.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
